import { useEffect, useState } from "react";
import api from "@/lib/api";
import { SERVICE_GROUPS, DISCOUNT_TIERS, DURATION_OPTIONS } from "@/lib/services";
import { formatCNPJ, formatCPF, formatBRL } from "@/lib/api";
import { Search, Plus, X, Trash2, Upload, FileText } from "lucide-react";
import { toast } from "sonner";

const blank = () => ({
  name: "",
  person_type: "PJ",
  document: "",
  services: [],
  partnership_type: "desconto",
  discount_tier: 20,
  price_table: [],
  duration_months: 12,
  start_date: new Date().toISOString().slice(0, 10),
  responsible: { name: "", cpf: "", role: "", phone: "", email: "" },
  contacts: { email: "", phone: "", whatsapp: "", site: "", instagram: "", sac: "" },
  address: { cep: "", street: "", number: "", complement: "", neighborhood: "", city: "", state: "AM" },
  description: "",
});

export default function CredenciadoForm({ partnerId, initial, onSaved, isPublic = false, submitToken = null }) {
  const [form, setForm] = useState(initial || blank());
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [lookup, setLookup] = useState(false);

  useEffect(() => { if (initial) setForm({ ...blank(), ...initial }); }, [initial]);

  const set = (path, value) => {
    setForm((f) => {
      const copy = { ...f };
      const parts = path.split(".");
      let cur = copy;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = { ...(cur[parts[i]] || {}) };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return copy;
    });
  };

  const toggleService = (s) => {
    set("services", form.services.includes(s) ? form.services.filter((x) => x !== s) : [...form.services, s]);
  };

  const fetchCNPJ = async () => {
    if (!form.document || form.document.replace(/\D/g, "").length !== 14) {
      toast.error("Digite um CNPJ válido (14 dígitos)");
      return;
    }
    setLookup(true);
    try {
      const { data } = await api.get(`/integrations/cnpj/${form.document.replace(/\D/g, "")}`);
      setForm((f) => ({
        ...f,
        name: data.nome_fantasia || data.razao_social || f.name,
        contacts: { ...f.contacts, email: data.email || f.contacts.email, phone: data.phone || f.contacts.phone },
        address: { ...(data.address || {}), ...Object.fromEntries(Object.entries(f.address).filter(([_, v]) => v && !data.address?.[_])) },
      }));
      toast.success(`Dados de ${data.razao_social} carregados`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao consultar CNPJ");
    } finally { setLookup(false); }
  };

  const addPriceRow = () => set("price_table", [...form.price_table, { procedure: "", balcao_price: 0, final_price: 0 }]);
  const updatePriceRow = (i, key, value) => {
    const copy = [...form.price_table];
    copy[i] = { ...copy[i], [key]: key === "procedure" ? value : parseFloat(value || 0) };
    set("price_table", copy);
  };
  const removePriceRow = (i) => set("price_table", form.price_table.filter((_, idx) => idx !== i));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        document: form.document.replace(/\D/g, ""),
      };
      if (form.partnership_type === "desconto") payload.price_table = [];
      if (form.partnership_type === "tabela") payload.discount_tier = null;

      let result;
      if (isPublic && submitToken) {
        result = await api.post(`/public/invitations/${submitToken}/submit`, payload);
        toast.success("Cadastro recebido! Aguarde a validação.");
      } else if (partnerId) {
        result = await api.put(`/partners/${partnerId}`, payload);
        toast.success("Credenciado atualizado!");
      } else {
        result = await api.post("/partners", payload);
        toast.success("Credenciado cadastrado! Pronto para gerar o contrato.");
      }
      onSaved?.(result.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally { setBusy(false); }
  };

  const stepValid = (s) => {
    if (s === 1) return form.person_type && form.document && form.document.replace(/\D/g, "").length >= (form.person_type === "PF" ? 11 : 14) && form.name;
    if (s === 2) return form.services.length > 0;
    if (s === 3) return form.partnership_type && (form.partnership_type === "desconto" ? form.discount_tier : form.price_table.length > 0);
    if (s === 4) return form.contacts.email && form.address.city && form.responsible.name;
    return true;
  };

  const StepHeader = ({ steps }) => (
    <div className="flex items-center gap-2 mb-8 overflow-x-auto" data-testid="form-stepper">
      {steps.map((s, i) => {
        const num = i + 1;
        const active = step === num;
        const done = step > num;
        return (
          <button key={s} onClick={() => setStep(num)} className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition ${active ? "bg-[#0E2A5C] text-white" : done ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`} data-testid={`step-${num}`}>
            <span className="h-5 w-5 rounded-full text-xs flex items-center justify-center bg-white/20">{num}</span>
            {s}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="credenciado-form">
      <StepHeader steps={["Identificação", "Serviços", "Parceria", "Endereço & Contato", "Revisão"]} />

      {/* STEP 1 */}
      {step === 1 && (
        <div className="card-soft p-6 space-y-5" data-testid="step-content-1">
          <div>
            <label className="block text-sm font-bold mb-2 text-[#0E2A5C]">Tipo de pessoa</label>
            <div className="grid grid-cols-2 gap-2 max-w-md">
              {["PJ", "PF"].map((t) => (
                <button key={t} type="button" onClick={() => set("person_type", t)} className={`py-3 rounded-xl border text-sm font-semibold transition ${form.person_type === t ? "border-[#2A8FE0] bg-blue-50 text-[#0E2A5C]" : "border-slate-200 text-slate-500 hover:border-slate-300"}`} data-testid={`person-type-${t}`}>
                  {t === "PF" ? "Pessoa Física" : "Pessoa Jurídica"}
                </button>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">{form.person_type === "PF" ? "CPF" : "CNPJ"}</label>
              <div className="flex gap-2">
                <input
                  value={form.person_type === "PF" ? formatCPF(form.document) : formatCNPJ(form.document)}
                  onChange={(e) => set("document", e.target.value.replace(/\D/g, ""))}
                  className="flex-1 px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]"
                  data-testid="form-document"
                />
                {form.person_type === "PJ" && (
                  <button type="button" onClick={fetchCNPJ} disabled={lookup} className="btn-primary text-sm whitespace-nowrap disabled:opacity-50" data-testid="cnpj-lookup-button">
                    <Search className="h-4 w-4" /> {lookup ? "..." : "Consultar"}
                  </button>
                )}
              </div>
              {form.person_type === "PJ" && <p className="text-xs text-slate-500 mt-1">Auto-preenchimento via Receita Federal (BrasilAPI)</p>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">{form.person_type === "PF" ? "Nome do credenciado" : "Razão social / Nome fantasia"}</label>
              <input value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="form-name" />
            </div>
          </div>
        </div>
      )}

      {/* STEP 2: SERVICES */}
      {step === 2 && (
        <div className="card-soft p-6 space-y-5" data-testid="step-content-2">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-display text-xl font-bold text-[#0E2A5C]">Serviços credenciados</h3>
              <p className="text-sm text-slate-500">Marque tudo que será oferecido aos titulares Solimões Card</p>
            </div>
            <span className="text-sm font-bold text-[#2A8FE0]" data-testid="services-count">{form.services.length} selecionado(s)</span>
          </div>
          <div className="space-y-5">
            {SERVICE_GROUPS.map((group) => (
              <div key={group.id} data-testid={`service-group-${group.id}`}>
                <div className="font-bold text-[#0E2A5C] text-sm uppercase tracking-wider mb-2">{group.label}</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {group.items.map((s) => {
                    const checked = form.services.includes(s);
                    return (
                      <label key={s} className={`flex items-start gap-2 p-3 rounded-xl border cursor-pointer transition text-sm ${checked ? "border-[#2A8FE0] bg-blue-50" : "border-slate-200 hover:border-slate-300"}`} data-testid={`service-${s}`}>
                        <input type="checkbox" checked={checked} onChange={() => toggleService(s)} className="mt-0.5 accent-[#2A8FE0]" />
                        <span>{s}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* STEP 3: PARTNERSHIP */}
      {step === 3 && (
        <div className="card-soft p-6 space-y-5" data-testid="step-content-3">
          <h3 className="font-display text-xl font-bold text-[#0E2A5C]">Modalidade de parceria</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {[["desconto","Desconto padrão","% sobre valor de balcão"], ["tabela","Tabela própria","Procedimentos com preço fixo"]].map(([id, label, desc]) => (
              <button key={id} type="button" onClick={() => set("partnership_type", id)} className={`p-5 rounded-2xl border text-left transition ${form.partnership_type===id ? "border-[#2A8FE0] bg-blue-50" : "border-slate-200 hover:border-slate-300"}`} data-testid={`partnership-${id}`}>
                <div className="font-bold text-[#0E2A5C]">{label}</div>
                <div className="text-xs text-slate-500 mt-1">{desc}</div>
              </button>
            ))}
          </div>

          {form.partnership_type === "desconto" && (
            <div>
              <label className="block text-sm font-medium mb-2">Percentual de desconto</label>
              <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                {DISCOUNT_TIERS.map((t) => (
                  <button key={t} type="button" onClick={() => set("discount_tier", t)} className={`py-2.5 rounded-xl border text-sm font-bold transition ${form.discount_tier===t ? "border-[#2A8FE0] bg-blue-50 text-[#0E2A5C]" : "border-slate-200 text-slate-500 hover:border-slate-300"}`} data-testid={`discount-tier-${t}`}>
                    {t}%
                  </button>
                ))}
              </div>
            </div>
          )}

          {form.partnership_type === "tabela" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium">Tabela de procedimentos</label>
                <button type="button" onClick={addPriceRow} className="text-sm inline-flex items-center gap-1 text-[#2A8FE0] hover:underline" data-testid="add-price-row"><Plus className="h-4 w-4" /> Adicionar</button>
              </div>
              <div className="space-y-2">
                {form.price_table.map((row, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`price-row-${i}`}>
                    <input placeholder="Procedimento" value={row.procedure} onChange={(e)=>updatePriceRow(i,"procedure",e.target.value)} className="col-span-6 px-3 py-2 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" />
                    <input type="number" step="0.01" placeholder="Balcão R$" value={row.balcao_price} onChange={(e)=>updatePriceRow(i,"balcao_price",e.target.value)} className="col-span-3 px-3 py-2 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" />
                    <input type="number" step="0.01" placeholder="Cliente R$" value={row.final_price} onChange={(e)=>updatePriceRow(i,"final_price",e.target.value)} className="col-span-2 px-3 py-2 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" />
                    <button type="button" onClick={()=>removePriceRow(i)} className="text-rose-500 hover:text-rose-700"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                {form.price_table.length === 0 && <div className="text-sm text-slate-500 italic">Nenhum procedimento. Clique em "Adicionar".</div>}
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4 pt-2">
            <div>
              <label className="block text-sm font-medium mb-2">Tempo de parceria</label>
              <div className="grid grid-cols-3 gap-2">
                {DURATION_OPTIONS.map((m) => (
                  <button key={m} type="button" onClick={() => set("duration_months", m)} className={`py-2.5 rounded-xl border text-sm font-bold transition ${form.duration_months===m ? "border-[#2A8FE0] bg-blue-50 text-[#0E2A5C]" : "border-slate-200 text-slate-500 hover:border-slate-300"}`} data-testid={`duration-${m}`}>
                    {m} meses
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Data de início</label>
              <input type="date" value={form.start_date} onChange={(e)=>set("start_date", e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="form-start-date" />
            </div>
          </div>
        </div>
      )}

      {/* STEP 4: ADDRESS + CONTACTS */}
      {step === 4 && (
        <div className="card-soft p-6 space-y-5" data-testid="step-content-4">
          <h3 className="font-display text-xl font-bold text-[#0E2A5C]">Responsável</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <input placeholder="Nome do responsável" value={form.responsible.name} onChange={(e)=>set("responsible.name", e.target.value)} className="px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="responsible-name" />
            <input placeholder="Cargo / função" value={form.responsible.role} onChange={(e)=>set("responsible.role", e.target.value)} className="px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="responsible-role" />
            <input placeholder="CPF" value={formatCPF(form.responsible.cpf)} onChange={(e)=>set("responsible.cpf", e.target.value.replace(/\D/g,""))} className="px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="responsible-cpf" />
            <input placeholder="Telefone" value={form.responsible.phone} onChange={(e)=>set("responsible.phone", e.target.value)} className="px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="responsible-phone" />
            <input type="email" placeholder="E-mail do responsável" value={form.responsible.email} onChange={(e)=>set("responsible.email", e.target.value)} className="sm:col-span-2 px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="responsible-email" />
          </div>

          <h3 className="font-display text-xl font-bold text-[#0E2A5C] pt-2">Endereço completo</h3>
          <div className="grid sm:grid-cols-12 gap-3">
            <input placeholder="CEP" value={form.address.cep} onChange={(e)=>set("address.cep", e.target.value)} className="sm:col-span-3 px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="address-cep" />
            <input placeholder="Logradouro" value={form.address.street} onChange={(e)=>set("address.street", e.target.value)} className="sm:col-span-7 px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="address-street" />
            <input placeholder="Nº" value={form.address.number} onChange={(e)=>set("address.number", e.target.value)} className="sm:col-span-2 px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="address-number" />
            <input placeholder="Complemento" value={form.address.complement} onChange={(e)=>set("address.complement", e.target.value)} className="sm:col-span-4 px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="address-complement" />
            <input placeholder="Bairro" value={form.address.neighborhood} onChange={(e)=>set("address.neighborhood", e.target.value)} className="sm:col-span-4 px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="address-neighborhood" />
            <input placeholder="Cidade" value={form.address.city} onChange={(e)=>set("address.city", e.target.value)} className="sm:col-span-3 px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="address-city" />
            <input placeholder="UF" value={form.address.state} onChange={(e)=>set("address.state", e.target.value.toUpperCase().slice(0,2))} className="sm:col-span-1 px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="address-state" />
          </div>

          <h3 className="font-display text-xl font-bold text-[#0E2A5C] pt-2">Contatos</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <input type="email" placeholder="E-mail principal *" value={form.contacts.email} onChange={(e)=>set("contacts.email", e.target.value)} className="px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="contact-email" />
            <input placeholder="Telefone" value={form.contacts.phone} onChange={(e)=>set("contacts.phone", e.target.value)} className="px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="contact-phone" />
            <input placeholder="WhatsApp" value={form.contacts.whatsapp} onChange={(e)=>set("contacts.whatsapp", e.target.value)} className="px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="contact-whatsapp" />
            <input placeholder="Site (opcional)" value={form.contacts.site} onChange={(e)=>set("contacts.site", e.target.value)} className="px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="contact-site" />
            <input placeholder="Instagram (@usuario)" value={form.contacts.instagram} onChange={(e)=>set("contacts.instagram", e.target.value)} className="px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="contact-instagram" />
            <input placeholder="SAC (opcional)" value={form.contacts.sac} onChange={(e)=>set("contacts.sac", e.target.value)} className="px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="contact-sac" />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1.5">Descrição (opcional)</label>
            <textarea value={form.description} onChange={(e)=>set("description", e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0] min-h-[80px]" data-testid="form-description" />
          </div>
        </div>
      )}

      {/* STEP 5: REVIEW */}
      {step === 5 && (
        <div className="card-soft p-6 space-y-4" data-testid="step-content-5">
          <h3 className="font-display text-xl font-bold text-[#0E2A5C]">Revise os dados</h3>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div className="rounded-xl border border-slate-100 p-4">
              <div className="text-xs uppercase font-bold text-slate-500 mb-2">Identificação</div>
              <div><b>{form.name}</b></div>
              <div>{form.person_type} • {form.person_type==="PF"?formatCPF(form.document):formatCNPJ(form.document)}</div>
            </div>
            <div className="rounded-xl border border-slate-100 p-4">
              <div className="text-xs uppercase font-bold text-slate-500 mb-2">Parceria</div>
              <div>{form.partnership_type==="desconto" ? `Desconto ${form.discount_tier}%` : `Tabela própria (${form.price_table.length} procedimentos)`}</div>
              <div>{form.duration_months} meses a partir de {form.start_date}</div>
            </div>
            <div className="rounded-xl border border-slate-100 p-4 sm:col-span-2">
              <div className="text-xs uppercase font-bold text-slate-500 mb-2">Serviços ({form.services.length})</div>
              <div className="flex flex-wrap gap-1">
                {form.services.map((s) => <span key={s} className="text-xs px-2 py-1 rounded-full bg-blue-50 text-[#0E2A5C]">{s}</span>)}
              </div>
            </div>
            {form.partnership_type === "tabela" && form.price_table.length > 0 && (
              <div className="rounded-xl border border-slate-100 p-4 sm:col-span-2">
                <div className="text-xs uppercase font-bold text-slate-500 mb-2">Tabela de preços</div>
                <table className="w-full text-xs">
                  <thead className="text-slate-500"><tr><th className="text-left py-1">Procedimento</th><th className="text-right">Balcão</th><th className="text-right">Cliente</th></tr></thead>
                  <tbody>
                    {form.price_table.map((r,i)=>(
                      <tr key={i} className="border-t border-slate-100"><td className="py-1">{r.procedure}</td><td className="text-right">{formatBRL(r.balcao_price)}</td><td className="text-right text-emerald-600 font-bold">{formatBRL(r.final_price)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="rounded-xl border border-slate-100 p-4">
              <div className="text-xs uppercase font-bold text-slate-500 mb-2">Responsável</div>
              <div>{form.responsible.name} ({form.responsible.role})</div>
              <div>{form.responsible.email} • {form.responsible.phone}</div>
            </div>
            <div className="rounded-xl border border-slate-100 p-4">
              <div className="text-xs uppercase font-bold text-slate-500 mb-2">Endereço</div>
              <div>{form.address.street} {form.address.number}, {form.address.neighborhood}</div>
              <div>{form.address.city}/{form.address.state} • CEP {form.address.cep}</div>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3">
        <button type="button" disabled={step === 1} onClick={() => setStep(step - 1)} className="btn-outline disabled:opacity-30" data-testid="step-prev">Voltar</button>
        {step < 5 ? (
          <button type="button" disabled={!stepValid(step)} onClick={() => setStep(step + 1)} className="btn-primary disabled:opacity-50" data-testid="step-next">Próximo</button>
        ) : (
          <button type="button" disabled={busy} onClick={submit} className="btn-primary disabled:opacity-50" data-testid="form-submit">
            {busy ? "Salvando..." : (isPublic ? "Enviar cadastro" : (partnerId ? "Salvar alterações" : "Cadastrar credenciado"))}
          </button>
        )}
      </div>
    </div>
  );
}
